import { useState, useCallback, useEffect, useRef } from "react";
import { measurementService } from "../services";
import toast from "react-hot-toast";

export const useMeasurements = (params = {}, user = null) => {
  // State management
  const [measurements, setMeasurements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Throttling and retry logic
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const THROTTLE_DELAY = 1000; // 1 second

  // Fetch measurements with throttling and retry logic
  const fetchMeasurements = useCallback(async () => {
    const now = Date.now();
    if (now - lastRequestTimeRef.current < THROTTLE_DELAY) {
      console.log("Throttling request");
      return;
    }
    lastRequestTimeRef.current = now;

    setIsLoading(true);
    try {
      // Build API parameters based on user role
      const apiParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      };

      // For Company Owner, only fetch measurements from their company
      if (user?.role?.id === 2 && user?.company?.id) {
        apiParams.companyId = user.company.id;
      }

      const response = await measurementService.getAll(apiParams);
      setMeasurements(response.data.measurements || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      // Handle rate limiting with retry
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`
          );

          setTimeout(() => {
            fetchMeasurements();
          }, retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please try again later.");
        }
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch measurements"
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, user, params]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        fetchMeasurements();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchMeasurements]);

  // Initial fetch
  useEffect(() => {
    fetchMeasurements();
  }, [currentPage]);

  // Create measurement
  const createMeasurement = useCallback(
    async (measurementData) => {
      setIsCreating(true);
      try {
        // Set isActive to true by default for new measurements
        const dataWithDefaults = { ...measurementData, isActive: true };
        await measurementService.create(dataWithDefaults);
        toast.success("Measurement created successfully!");
        setShowModal(false);
        setEditingMeasurement(null);
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create measurement"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchMeasurements]
  );

  // Update measurement
  const updateMeasurement = useCallback(
    async (id, measurementData) => {
      setIsUpdating(true);
      try {
        await measurementService.update(id, measurementData);
        toast.success("Measurement updated successfully!");
        setShowModal(false);
        setEditingMeasurement(null);
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update measurement"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchMeasurements]
  );

  // Delete measurement
  const deleteMeasurement = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await measurementService.delete(id);
        toast.success("Measurement deleted successfully!");
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete measurement"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchMeasurements]
  );

  // Toggle status
  const toggleStatus = useCallback(
    async (id) => {
      setIsToggling(true);
      try {
        await measurementService.toggleStatus(id);
        toast.success("Measurement status updated successfully!");
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update measurement status"
        );
      } finally {
        setIsToggling(false);
      }
    },
    [fetchMeasurements]
  );

  // Handlers
  const handleEdit = useCallback((measurement) => {
    setEditingMeasurement(measurement);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (measurement) => {
      if (window.confirm("Are you sure you want to delete this measurement?")) {
        deleteMeasurement(measurement.id);
      }
    },
    [deleteMeasurement]
  );

  const handleToggleStatus = useCallback(
    (measurement) => {
      toggleStatus(measurement.id);
    },
    [toggleStatus]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingMeasurement(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      console.log("useMeasurements - handleSubmit called with data:", data);
      console.log("useMeasurements - data.id:", data.id);
      console.log("useMeasurements - editingMeasurement:", editingMeasurement);

      if (data.id) {
        console.log(
          "useMeasurements - Calling updateMeasurement with ID:",
          data.id
        );
        await updateMeasurement(data.id, data);
      } else {
        console.log("useMeasurements - Calling createMeasurement");
        await createMeasurement(data);
      }
    },
    [createMeasurement, updateMeasurement, editingMeasurement]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchMeasurements();
  }, [fetchMeasurements]);

  const refreshMeasurements = useCallback(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  return {
    // Data
    measurements,
    isLoading,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,

    // Modal state
    showModal,
    editingMeasurement,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createMeasurement,
    updateMeasurement,
    deleteMeasurement,
    toggleStatus,
    fetchMeasurements,
    refreshMeasurements,

    // Search
    searchTerm,
    setSearchTerm,
  };
};

export default useMeasurements;
